
import React, { useState } from "react";
import { ArrowRight, Info, Clock, CreditCard, Award, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToastWrapper } from "@/hooks/useToastWrapper";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import OptimizedImage from "@/components/ui/optimized-image";
import { useIsMobile } from "@/hooks/use-mobile";

interface PartnerCardProps {
  name: string;
  link: string;
  description: string;
  highlight?: string;
  imagePlaceholder?: boolean;
  expandedInfo?: string;
  loanExample?: string;
  recommended?: boolean;
  isFixura?: boolean;
  amount?: string;
}

export const PartnerCard = ({
  name,
  link,
  description,
  highlight,
  imagePlaceholder = true,
  expandedInfo,
  loanExample,
  recommended = false,
  isFixura = false,
  amount
}: PartnerCardProps) => {
  const { toast } = useToastWrapper();
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useIsMobile();

  const handleInfoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (expandedInfo) {
      setIsExpanded(true);
    } else {
      toast(`${name}: ${description}`);
    }
  };

  const handleCloseDialog = () => {
    setIsExpanded(false);
  };

  const getPartnerLogo = () => {
    if (name === "Sortter") {
      return "/lovable-uploads/f512bee0-9b77-4fcd-a854-95f382fc1bf1.png";
    } else if (isFixura) {
      return "/lovable-uploads/aeffd7af-9fba-426d-af23-0df19fc54931.png";
    } else if (name === "Creditstar") {
      return "/lovable-uploads/e881666b-5fcd-40df-965a-e666b962d605.png";
    } else if (name === "Bondora") {
      return "/lovable-uploads/380c5f16-935d-4d4f-b0f5-35ba56aa59aa.png";
    } else if (name === "Ferratum") {
      return "/lovable-uploads/f8e5c4b1-7f9e-48d5-a269-f403fadadf6f.png";
    } else if (name === "Axolaina") {
      return "/lovable-uploads/6ec4385b-463a-4b93-a2b1-93211df83cb5.png";
    } else if (name === "Unolaina") {
      return "/lovable-uploads/eb69ff3c-e053-42fa-bc37-665abec7189d.png";
    } else if (name === "Rensa") {
      return "/lovable-uploads/f284367d-e3dd-4e7d-90f2-04660a6ca2a2.png";
    } else if (name === "Minunraha") {
      return "/lovable-uploads/d0f9e897-64e4-4b5a-83d3-c34a49ccb58d.png";
    } else if (name === "Cashday") {
      return "/lovable-uploads/192f4997-4aeb-4659-9e3d-5b58ada96e44.png";
    } else if (name === "Neolaina") {
      return "/lovable-uploads/e70d8d00-a476-4ce3-b0f9-8c0d7f2fb793.png";
    } else if (name === "iRaha") {
      return "/lovable-uploads/800be136-b9d4-4832-9b3f-f0a1b6425a45.png";
    } else if (name === "Northmill") {
      return "/lovable-uploads/9dabe9c6-8c86-4d42-b80a-6a51c1dddd3d.png";
    } else if (name === "Lainasto") {
      return "/lovable-uploads/6b3cf9d5-ca4a-4e34-acf9-dc4dc45faf59.png";
    } else if (name === "Qred") {
      return "/lovable-uploads/a2efd10c-f298-4a1a-8097-30ad0c4c975c.png";
    } else if (name === "Bluestep") {
      return "/lovable-uploads/01da9111-1348-4bf2-bea4-df914538f8d3.png";
    } else if (name === "Alisa Pankki") {
      return "/lovable-uploads/ca2bf19a-32a5-48fb-9e8c-6cb3f61b8ce9.png";
    } else if (name === "SweepBank") {
      return "/lovable-uploads/36ba7093-3deb-443f-9f9c-c6ccda209996.png";
    } else {
      return null;
    }
  };

  const partnerLogo = getPartnerLogo();
  
  const needsSpecialBackground = () => {
    return name === "Alisa Pankki" || name === "Northmill";
  };
  
  const getSpecialBackgroundColor = () => {
    if (name === "Alisa Pankki") {
      return "bg-white";
    } else if (name === "Northmill") {
      return "bg-[#80d8e4]";
    }
    return "";
  };

  return <>
      <Card className={`overflow-hidden h-full transition-all hover:shadow-lg ${recommended ? 'border-2 border-blue-400 ring-2 ring-blue-200' : 'border-blue-100'}`}>
        <div className="relative">
          {imagePlaceholder && <div className={`h-48 ${recommended ? 'bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100' : 'bg-gradient-to-r from-blue-100 to-purple-100'} flex items-center justify-center`}>
              {partnerLogo ? (
                <div className={`flex items-center justify-center w-full h-full ${needsSpecialBackground() ? getSpecialBackgroundColor() : ''}`}>
                  <div className={`w-full ${isMobile ? 'max-w-[80%]' : 'max-w-[240px]'} mx-auto px-2`}>
                    <div className={`${isMobile ? 'h-[140px]' : 'h-[100px]'} flex items-center justify-center`}>
                      <OptimizedImage 
                        src={partnerLogo} 
                        alt={`${name} logo`} 
                        className={`max-h-[120px] ${name === "Alisa Pankki" ? 'max-w-[140px]' : 'max-w-full'}`}
                        objectFit="contain"
                        priority={false}
                        quality={75}
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-blue-700 font-semibold text-xl">{name}</span>
              )}
              
              {amount && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-1.5 px-3">
                  <p className="text-center text-white font-bold">{amount}</p>
                </div>}
            </div>}
          {recommended && <div className="absolute top-2 left-2">
              <div className="bg-blue-600 text-white text-xs py-1 px-2 rounded-full flex items-center">
                <Award className="h-3 w-3 mr-1" />
                Suosittelemme
              </div>
            </div>}
          <div className="absolute top-3 right-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={handleInfoClick} className="bg-white p-1.5 rounded-full shadow-md hover:bg-blue-50 relative">
                    <Info className="h-4 w-4 text-blue-600" />
                    {loanExample && <span className="absolute -top-1 -right-1 bg-red-500 rounded-full w-2 h-2"></span>}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Katso lisätiedot {loanExample ? "ja lainaesimerkki" : ""}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <CardContent className="p-5">
          <h3 className="font-semibold text-lg mb-1 text-blue-700">{name}</h3>
          
          {highlight && <div className="mb-3 text-purple-700 font-medium flex items-center">
              <CreditCard className="h-4 w-4 mr-1 inline-block" /> 
              <span className="text-sm">{highlight}</span>
            </div>}
          
          <p className="text-gray-600 text-sm mb-4 min-h-[60px]">{description}</p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-xs text-green-600 font-medium">
              <Clock className="h-3 w-3 mr-1" /> Nopea päätös
            </div>
            
            <Button size="sm" asChild className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
              <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1">
                <span className="text-white font-medium text-sm">Hae nyt</span> <ArrowRight className="h-4 w-4 text-white" />
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {expandedInfo && <Dialog open={isExpanded} onOpenChange={handleCloseDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl text-blue-700">{name} - Lisätiedot</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="mb-4">
                {partnerLogo ? (
                  <div className={`w-full mx-auto flex justify-center ${needsSpecialBackground() ? getSpecialBackgroundColor() : ''}`}>
                    <div className="h-[120px] flex items-center justify-center">
                      <OptimizedImage 
                        src={partnerLogo} 
                        alt={`${name} - palvelun esittely`} 
                        className="max-h-[120px] max-w-full"
                        objectFit="contain"
                        priority={false}
                        quality={75}
                      />
                    </div>
                  </div>
                ) : (
                  <img 
                    src={`https://images.unsplash.com/photo-1581091226825-a6a2a5aee158`} 
                    alt={`${name} - palvelun esittely`} 
                    className="w-full h-48 object-cover rounded-md mb-4" 
                  />
                )}
              </div>
              <div className="text-gray-700">
                <h3 className="font-semibold text-blue-700 mb-2">Tietoa palvelusta</h3>
                <p className="mb-3">{expandedInfo}</p>
                
                {loanExample && <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 text-sm">
                    <div className="flex items-center mb-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2" />
                      <h4 className="font-semibold text-yellow-800">Lainaesimerkki</h4>
                    </div>
                    <p className="text-yellow-800">{loanExample}</p>
                  </div>}
                
                <p className="text-sm text-gray-500 mt-4">Lainaa hakiessasi on tärkeää tutustua lainaehtoihin huolellisesti. Muista, että lainan korko ja ehdot määräytyvät aina yksilöllisesti.</p>
              </div>
              <div className="mt-2">
                <Button asChild className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
                  <a href={link} target="_blank" rel="noopener noreferrer">
                    <span className="text-white">Hae lainaa</span> <ArrowRight className="h-4 w-4 ml-1 text-white" />
                  </a>
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>}
    </>;
};
